// SessionOnly.tsx — fix-wave item 2. The session guard `Protected.tsx` runs, WITHOUT the
// bot-scoped shell — for `/onboarding` only. Spec §8: "`/onboarding`. Stays outside the shell,
// full-width, unchanged. A zero-bot workspace has no bot to scope a rail to, and the wizard is
// a deliberately focused flow." `App.tsx` nested `/onboarding` inside `<Protected>` anyway, and
// `Protected` now renders the rail unconditionally, so every brand-new signup (100% of them —
// `resolveLandingPath` sends a zero-bot workspace straight here) landed on the wizard with a
// rail scoped to no bot at all: `BotSwitcher` returns `null`, every `RailNav` item renders
// `aria-disabled`, and `UsageBlock` shows a Free-plan upsell wrapped around the golden path.
//
// This duplicates `Protected`'s session-pending/no-session guard and impersonation banner
// rather than sharing a hook with it — the dispatch for this fix wave is explicit that
// `Protected` itself should not otherwise be redesigned, and the guard is small enough that
// two copies read more plainly than a shared abstraction built for exactly two callers.
import { Navigate, Outlet } from 'react-router';
import { Skeleton } from '@repo/ui/components/skeleton';
import { authClient, useSession } from '../lib/auth-client';
import { useT } from '../locales/context';

/** Not a bare "Loading…" (handoff README §9, which calls out `Protected.tsx`'s session check
 * by name as a place that string must not survive) — stands in for the wizard's own ~680px
 * centered step card, since this layout renders no rail/top-bar chrome to skeleton instead. */
function SessionOnlySkeleton() {
  return (
    <div className="mx-auto flex min-h-screen w-full max-w-[680px] flex-col justify-center gap-5 px-4 py-12 sm:px-6" aria-hidden="true">
      <Skeleton className="h-7 w-2/3" />
      <Skeleton className="h-64 w-full rounded-[14px]" />
    </div>
  );
}

export function SessionOnly() {
  const { data: session, isPending } = useSession();
  const t = useT();

  if (isPending) return <SessionOnlySkeleton />;
  if (!session) return <Navigate to="/sign-in" replace />;

  // Same impersonation-banner behaviour `Protected.tsx` gives every other authed screen — an
  // admin impersonating a zero-bot workspace to help with onboarding shouldn't lose the "you
  // are impersonating" context and the way back out just because this one route has no rail.
  const impersonatedBy = session.session.impersonatedBy;

  function handleStopImpersonating() {
    // Back to /admin, not the owner dashboard — exiting impersonation resumes ADMIN work
    // (Johnny, 2026-08-20). Hard navigation for the same cache-reset reason as entry.
    authClient.admin.stopImpersonating().then(() => location.assign('/admin'));
  }

  return (
    <div className="flex min-h-screen flex-col">
      {impersonatedBy && (
        <div
          className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-center text-[13px] font-medium"
          style={{
            color: 'var(--st-paused)',
            backgroundColor: 'color-mix(in srgb, var(--st-paused) var(--st-wash), transparent)',
          }}
        >
          <span>{t.shell.impersonating({ email: session.user.email })}</span>
          <button
            type="button"
            onClick={handleStopImpersonating}
            className="font-semibold underline underline-offset-2"
          >
            {t.shell.stopImpersonating}
          </button>
        </div>
      )}
      <Outlet />
    </div>
  );
}
